import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import {
  SMART_INSIGHTS_ARCHIVE_REASONS,
  type SmartInsightsArchiveReason,
} from 'lumen/utils/constants/smart-insights';

import styles from './dropdown-archive.module.css';

interface GeneralSmartInsightsDetailViewDropdownArchiveSignature {
  Args: {
    isArchiving?: boolean;
    onArchive: (reason: SmartInsightsArchiveReason) => void;
    recommendationCount: number;
  };
}

type ArchiveOption = Option & {
  label: string;
  value: SmartInsightsArchiveReason;
};

export default class GeneralSmartInsightsDetailViewDropdownArchive extends Component<GeneralSmartInsightsDetailViewDropdownArchiveSignature> {
  @service declare intl: Services['intl'];

  get options(): ArchiveOption[] {
    if (this.args.recommendationCount === 0) {
      return [
        {
          label: this.intl.t('smart_insights.archive.action_taken'),
          value: SMART_INSIGHTS_ARCHIVE_REASONS.ACTION_TAKEN,
        },
        {
          label: this.intl.t('smart_insights.archive.not_actionable'),
          value: SMART_INSIGHTS_ARCHIVE_REASONS.NOT_ACTIONABLE,
        },
      ];
    }

    const actionedLabel =
      this.args.recommendationCount === 1
        ? this.intl.t('smart_insights.archive.actioned_recommendation')
        : this.intl.t('smart_insights.archive.actioned_recommendations');

    return [
      {
        label: actionedLabel,
        value: SMART_INSIGHTS_ARCHIVE_REASONS.ACTIONED_RECOMMENDATIONS,
      },
      {
        label: this.intl.t('smart_insights.archive.actioned_other'),
        value: SMART_INSIGHTS_ARCHIVE_REASONS.ACTIONED_OTHER,
      },
      {
        label: this.intl.t('smart_insights.archive.not_actionable'),
        value: SMART_INSIGHTS_ARCHIVE_REASONS.NOT_ACTIONABLE,
      },
    ];
  }

  @action onChange(option: ArchiveOption): void {
    this.args.onArchive(option.value);
  }

  <template>
    <div class="ic-product-tour-57546189-smart-insights-archive">
      {{#if @isArchiving}}
        <PrismButton
          @isDisabled={{true}}
          @isLoading={{true}}
          @isSmall={{true}}
          @label={{t "smart_insights.archive.button_label"}}
          data-test-id="dropdown-archive-loading"
        />
      {{else}}
        <div class={{styles.dropdown}}>
          <PrismSelectInput
            @isSmall={{true}}
            @onChange={{this.onChange}}
            @options={{this.options}}
            @placeholder={{t "smart_insights.archive.button_label"}}
            data-test-id="dropdown-archive"
          />
        </div>
      {{/if}}
    </div>
  </template>
}
